import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { FacilitySearchService, ProviderSearchService } from '../api/services';
import { FacilitySearchService as FacilitySearchServiceV2, ProviderSearchService as  ProviderSearchServiceV2} from '../apiV2/services';

/**
 * This module intercepts outgoing calls and replaces the endpoints for provider and facility searches with the APIM roots retrieved from the configuration.
 */
@Injectable({
  providedIn: 'root'
})
export class ApiBaseInterceptorService implements HttpInterceptor {
  endpointsToOverride: string[];
  endpointsToOverrideV2: string[];
  constructor() {
    // programmatically build endpoints to override
    this.endpointsToOverride = [
      ...Object.getOwnPropertyNames(ProviderSearchService)
        .filter((prop) => typeof (ProviderSearchService as any)[prop] === "string")
        .map((prop) => (ProviderSearchService as any)[prop]),

      ...Object.getOwnPropertyNames(FacilitySearchService)
        .filter((prop) => typeof (FacilitySearchService as any)[prop] === "string")
        .map((prop) => (FacilitySearchService as any)[prop])
    ];

    this.endpointsToOverrideV2 = [
      ...Object.getOwnPropertyNames(ProviderSearchServiceV2)
        .filter((prop) => typeof (ProviderSearchServiceV2 as any)[prop] === "string")
        .map((prop) => (ProviderSearchServiceV2 as any)[prop]),

      ...Object.getOwnPropertyNames(FacilitySearchServiceV2)
        .filter((prop) => typeof (FacilitySearchServiceV2 as any)[prop] === "string")
        .map((prop) => (FacilitySearchServiceV2 as any)[prop])
    ]
  }

  /**
   * The override of the interception logic.
   * Which is to replace the base part of the API calls with the root of the APIM end point
   * read from the environments object.
   * @param req http request object.
   * @param next the next http handler of the pipeline.
   * @returns the pipeline of http handlers
   */
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Add global Accept-Language header based on user selection (if not already present)
    const selectedLanguage = (typeof localStorage !== 'undefined' ? localStorage.getItem('translateLanguageTo') : null) || 'en';
    const reqWithLanguage = req.headers.has('Accept-Language')
      ? req
      : req.clone({ setHeaders: { 'Accept-Language': selectedLanguage } });

    // get apim base uri
    const baseUri = environment.apimOverrideUri;
    const baseUriV2 = environment.apimOverrideUriV2;
    const baseUriV3 = environment.apiV2BaseUri;
    const ticketBaseUri = baseUriV2 || 'https://api-test.getconvergent.io/carefindr/apim3';

    // ET: for the detail calls, the path is a pattern which contains token {uid}
    // see if request has a match in our overridable endpoints
    const match = this.endpointsToOverride.find(endpointPattern => new RegExp(endpointPattern.replace('{uid}', '[\\w%]+')).test(reqWithLanguage.url));
    const matchV2 = this.endpointsToOverrideV2.find(endpointPattern => new RegExp(endpointPattern.replace('{uid}', '[\\w%]+')).test(reqWithLanguage.url));

    // New: Special handling for V3 Carefindr endpoints
    // Facility print-all (V3)
    if (reqWithLanguage.url.includes('/v3/facility/printall-carefindr') && baseUriV3 !== '') {
      const rootUrlPattern = /^https?:\/\/[^\/]+\/api/;
      const endpoint = reqWithLanguage.url.replace(rootUrlPattern, '');

      const newReq = reqWithLanguage.clone({
        url: baseUriV3 + endpoint
      });

      return next.handle(newReq);
    }

    // Provider NL search (V3)
    if (reqWithLanguage.url.includes('/v3/provider/nl-search-carefindr') && baseUriV3 !== '') {
      // You may want a regex match if there could be variable params in the path
      const rootUrlPattern = /^https?:\/\/[^\/]+\/api/;
      const endpoint = reqWithLanguage.url.replace(rootUrlPattern, '');

      const newReq = reqWithLanguage.clone({
        url: baseUriV3 + endpoint
      });

      return next.handle(newReq);
    }

    // Ticket API should use APIM3 only.
    if (reqWithLanguage.url.includes('/ticket') && ticketBaseUri !== '') {
      const rootUrlPattern = /^https?:\/\/[^\/]+\/api/;
      const endpoint = reqWithLanguage.url.replace(rootUrlPattern, '');
      const newReq = reqWithLanguage.clone({
        url: ticketBaseUri + endpoint
      });
      return next.handle(newReq);
    }

    if (baseUriV2 !== '' && matchV2) {
      const rootUrlPattern = /^https?:\/\/[^\/]+\/api/;
      const endpoint = reqWithLanguage.url.replace(rootUrlPattern, '');

      const newReq = reqWithLanguage.clone({
        url: baseUriV2 + endpoint
      });

      return next.handle(newReq);
    } else if (baseUri !== '' && match) {
      const rootUrlPattern = /^https?:\/\/[^\/]+\/api/;
      const endpoint = reqWithLanguage.url.replace(rootUrlPattern, '');

      const newReq = reqWithLanguage.clone({
        url: baseUri + endpoint
      });

      return next.handle(newReq);
    }

    return next.handle(reqWithLanguage);
  }
}
